import React, { useState, useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import { parsePhoneNumber } from "libphonenumber-js";

const COUNTRY_LIST = [
  { name: "Botswana", dialCode: "+267", code: "BW" },
  { name: "South Africa", dialCode: "+27", code: "ZA" },
  { name: "Zambia", dialCode: "+260", code: "ZM" },
  { name: "Zimbabwe", dialCode: "+263", code: "ZW" },
  { name: "Namibia", dialCode: "+264", code: "NA" },
  { name: "Mozambique", dialCode: "+258", code: "MZ" },
  { name: "Kenya", dialCode: "+254", code: "KE" },
  { name: "Nigeria", dialCode: "+234", code: "NG" },
  { name: "Ghana", dialCode: "+233", code: "GH" },
  { name: "United States", dialCode: "+1", code: "US" },
];

const LOCAL_LENGTHS = {
  BW: 8, // Botswana mobiles (e.g. 71928306)
  ZA: 9,
  ZM: 9,
  ZW: 9,
  NA: 8,
  MZ: 8,
  KE: 9,
  NG: 10,
  GH: 9,
  US: 10,
};

export default function HotelFormStep1A({ hotelData, setHotelData, errors }) {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isCustomCode, setIsCustomCode] = useState(false);
  const [customDialCode, setCustomDialCode] = useState("");
  const [phoneWarning, setPhoneWarning] = useState("");

  // Initialize country selection
  useEffect(() => {
    if (hotelData.CountryDialCode) {
      const match = COUNTRY_LIST.find(c => c.dialCode === hotelData.CountryDialCode);
      if (match) {
        setSelectedCountry(match);
        setIsCustomCode(false);
      } else {
        setIsCustomCode(true);
        setCustomDialCode(hotelData.CountryDialCode);
        setSelectedCountry(null);
      }
    } else {
      setSelectedCountry(COUNTRY_LIST[0]);
      setHotelData(prev => ({
        ...prev,
        CountryDialCode: COUNTRY_LIST[0].dialCode,
        Country: COUNTRY_LIST[0].name // Set country name as well
      }));
    }
  }, [hotelData.CountryDialCode, setHotelData]);

  // Enhanced phone validation
  const validatePhone = (dialCode, number, isoCode) => {
    if (!number) return false;
    
    // First try with libphonenumber
    try {
      const phone = parsePhoneNumber(`${dialCode}${number}`);
      if (phone?.isValid()) return true;
    } catch (_) {
      // Fall through to fallback validation
    }
    
    // Fallback validation
    const expectedLength = LOCAL_LENGTHS[isoCode];
    if (expectedLength) {
      const digitsOnly = number.replace(/\D/g, "");
      return digitsOnly.length === expectedLength;
    }
    
    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setHotelData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountrySelect = (e) => {
    const value = e.target.value;
    if (value === "other") {
      setIsCustomCode(true);
      setSelectedCountry(null);
      setHotelData(prev => ({ 
        ...prev, 
        CountryDialCode: "",
        Country: ""
      }));
    } else {
      const country = COUNTRY_LIST.find(c => c.name === value);
      setIsCustomCode(false);
      setSelectedCountry(country);
      setHotelData(prev => ({
        ...prev,
        CountryDialCode: country.dialCode,
        Country: country.name
      }));
    }
  };

  const handleCustomDialCodeChange = (e) => {
    const code = e.target.value;
    setCustomDialCode(code);
    setHotelData(prev => ({ 
      ...prev, 
      CountryDialCode: code,
      Country: "Other"
    }));
  };

  const handleContactNumberChange = (e) => {
    const value = e.target.value;
    setHotelData(prev => ({ ...prev, ContactNumber: value }));
    
    if (value) {
      const valid = validatePhone(
        hotelData.CountryDialCode,
        value,
        selectedCountry?.code
      );
      setPhoneWarning(valid ? "" : "Invalid phone number for selected country");
    } else {
      setPhoneWarning("");
    }
  };

  // Re-validate when dial code changes
  useEffect(() => {
    if (hotelData.ContactNumber) {
      const valid = validatePhone(
        hotelData.CountryDialCode,
        hotelData.ContactNumber,
        selectedCountry?.code
      );
      setPhoneWarning(valid ? "" : "Invalid phone number for selected country");
    }
  }, [hotelData.CountryDialCode, hotelData.ContactNumber, selectedCountry]);

  const propertyType = (hotelData.Type || "Property").replace(/^./, c => c.toUpperCase());

  return (
    <div className="hotel-form-step">
      <h2>{propertyType} Information</h2>

      <div className="form-group">
        <label>{propertyType} Name</label>
        <input
          type="text"
          name="HotelName"
          value={hotelData.HotelName || ""}
          onChange={handleChange}
          required
          className={errors?.HotelName ? "error" : ""}
        />
        {errors?.HotelName && (
          <p className="error-message">{errors.HotelName}</p>
        )}
      </div>

      <div className="form-group">
        <label>Postal Address</label>
        <input
          type="text"
          name="Address"
          value={hotelData.Address || ""}
          onChange={handleChange}
          required
          className={errors?.Address ? "error" : ""}
        />
        {errors?.Address && <p className="error-message">{errors.Address}</p>}
      </div>

      <div className="form-group">
        <label>Contact Number</label>
        
        <select
          className={`country-select ${errors?.CountryDialCode ? "error" : ""}`}
          onChange={handleCountrySelect}
          value={isCustomCode ? "other" : selectedCountry?.name}
        >
          {COUNTRY_LIST.map(({ name, dialCode }) => (
            <option key={name} value={name}>
              {name} ({dialCode})
            </option>
          ))}
          <option value="other">Other / Not Listed</option>
        </select>

        {!isCustomCode && selectedCountry && (
          <div className="phone-input-container">
            <ReactCountryFlag
              countryCode={selectedCountry.code}
              svg
              style={{ width: "1.5em", height: "1.5em", marginRight: "8px" }}
              aria-label={selectedCountry.name}
            />
            <span className="country-code">{selectedCountry.dialCode}</span>
            <input
              type="text"
              name="ContactNumber"
              value={hotelData.ContactNumber || ""}
              onChange={handleContactNumberChange}
              placeholder="Enter local number"
              required
              className={errors?.ContactNumber ? "error" : ""}
            />
          </div>
        )}

        {isCustomCode && (
          <div className="phone-input-container">
            <input
              className={`country-code-input ${errors?.CountryDialCode ? "error" : ""}`}
              value={customDialCode}
              onChange={handleCustomDialCodeChange}
              placeholder="+XXX"
              required
            />
            <input
              type="text"
              name="ContactNumber"
              value={hotelData.ContactNumber || ""}
              onChange={handleContactNumberChange}
              placeholder="Enter phone number"
              required
              className={errors?.ContactNumber ? "error" : ""}
            />
          </div>
        )}

        {errors?.ContactNumber && (
          <p className="error-message">{errors.ContactNumber}</p>
        )}
        {errors?.CountryDialCode && (
          <p className="error-message">{errors.CountryDialCode}</p>
        )}
        {!errors?.ContactNumber && phoneWarning && (
          <p className="warning-message">
            <WarningIcon /> {phoneWarning}
          </p>
        )}
      </div>

      <div className="form-group">
        <label>Physical Location (Plot Number)</label>
        <input
          type="text"
          name="PhysicalLocation"
          value={hotelData.PhysicalLocation || ""}
          onChange={handleChange}
          required
          className={errors?.PhysicalLocation ? "error" : ""}
        />
        {errors?.PhysicalLocation && (
          <p className="error-message">{errors.PhysicalLocation}</p>
        )}
      </div>

      <div className="form-group">
        <label>Town/Village</label>
        <input
          type="text"
          name="TownOrVillage"
          value={hotelData.TownOrVillage || ""}
          onChange={handleChange}
          required
          className={errors?.TownOrVillage ? "error" : ""}
        />
        {errors?.TownOrVillage && (
          <p className="error-message">{errors.TownOrVillage}</p>
        )}
      </div>

      <style jsx>{`
        .hotel-form-step {
          max-width: 600px;
          margin: 0 auto;
        }
        .form-group {
          margin-bottom: 1.5rem;
        }
        label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }
        input, select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }
        .error {
          border-color: #ff0000;
        }
        .error-message {
          color: #ff0000;
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }
        .warning-message {
          color: #b8860b;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .phone-input-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        .country-code {
          font-weight: bold;
          white-space: nowrap;
        }
        .country-code-input {
          width: 80px;
        }
        .country-select {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}

function WarningIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
        fill="#b8860b"
      />
    </svg>
  );
}